// src/views/admin/AnalyticsPage.js
// --- UPDATE (Analytics Hub — AI, live, export) -----------------------------------
// • AI briefing (AnalyticsAIBriefing): KPIs vs the previous period, findings with evidence and
//   links, model status and a narrative from FlowSense AI — refreshed when live events arrive.
// • Ask FlowSense AI drawer: the explainer, focused on the Analytics Hub, seeded from the briefing.
// • Live: one websocket subscription (useAnalyticsLive) → live status badge, AI activity drawer
//   with unread count, and `liveVersion` / `lastLiveEvent` passed to every dashboard. With
//   "Live sync" on, the simple dashboards also refetch after relevant events (at most every 2 min).
// • New "AI Intelligence" tab: every model and engine (same overview as the AI Intelligence Center).
// • Tabs and the date range are in the URL (?tab=&from=&to=) — shareable, survive reloads.
// • Saved presets persist (localStorage) and are named in a dialog (no window.prompt);
//   presets can be deleted. Settings (auto-refresh, live sync, briefing) persist too.
// • Export works: KPI history CSV for the range, the AI briefing as JSON, or print / PDF.
// • Alt+1…Alt+0 switch tabs, Alt+R refreshes. Fake 500 ms "loading" bar on tab change removed;
//   tab icons are distinct.
// Every dashboard receives: dateRange, liveVersion, lastLiveEvent, onAskAI (extra props are
// ignored by dashboards that do not use them yet).
// --- UPDATE (round 3): FlowSense AI Guide button (tour of the open tab + the AI on it, live model
// status, quick questions into Ask FlowSense AI). data-guide anchors mark what the tour points at.
// --- END UPDATE ------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Heading, Tabs, TabList, TabPanels, Tab, TabPanel, HStack, VStack,
  Input, FormLabel, FormControl, FormHelperText, useColorModeValue, useColorMode, Icon,
  IconButton, Tooltip, Badge, Button, Text, SimpleGrid, Stat, StatLabel,
  StatNumber, Menu, MenuButton, MenuList, MenuItem, MenuDivider, useToast,
  Flex, Select, Switch, Divider, useDisclosure, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  Alert, AlertIcon, Collapse, Drawer, DrawerOverlay, DrawerContent, DrawerHeader,
  DrawerBody, DrawerCloseButton, Kbd,
} from '@chakra-ui/react';
import {
  MdCalendarToday, MdRefresh, MdDownload, MdSettings, MdAnalytics,
  MdDashboard, MdTrendingUp, MdBookmark, MdHistory, MdCached, MdAttachMoney, MdTimer,
  MdMonitorHeart, MdInventory, MdHealthAndSafety, MdPsychology, MdAutoAwesome, MdBolt,
  MdAssignment, MdDelete, MdPrint, MdTableChart, MdDataObject, MdHub,
} from 'react-icons/md';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ExecutiveOverviewDashboard from '../../components/analytics/ExecutiveOverviewDashboard';
import WorkOrderDeepDiveDashboard from '../../components/analytics/WorkOrderDeepDiveDashboard';
import AssetPerformanceDashboard from '../../components/analytics/AssetPerformanceDashboard';
import InventoryIntelligenceDashboard from '../../components/analytics/InventoryIntelligenceDashboard';
import PredictiveHealthDashboard from '../../components/analytics/PredictiveHealthDashboard';
import AILearningDashboard from '../../components/analytics/AILearningDashboard';
import LifecycleCostWidget from '../../components/analytics/LifecycleCostWidget';
import SlaComplianceDashboard from '../../components/analytics/SlaComplianceDashboard';

// --- UPDATE: Import the new ML Health & Telemetry Dashboard ---
import MlHealthDashboard from '../../components/analytics/MlHealthDashboard';
// --- END UPDATE ---

// --- UPDATE (Analytics Hub) ---
import AnalyticsAIBriefing from '../../components/analytics/AnalyticsAIBriefing';
import AnalyticsLiveFeed, { STATUS_META } from '../../components/analytics/AnalyticsLiveFeed';
import FlowSenseAIExplainer from '../../components/ml/FlowSenseAIExplainer';
import FlowSenseAIGuide from '../../components/ml/FlowSenseAIGuide'; // --- UPDATE (round 3)
import IntelligenceOverview from '../../components/ml/IntelligenceOverview';
import { currentUser, canManageModels } from '../../components/ml/mlFormat';
import useAnalyticsLive from '../../hooks/useAnalyticsLive';
import { useMlOverview } from '../../hooks/useMlOps';
import mlOpsService from '../../services/mlOpsService';

const PRESETS_KEY = 'flowsense.analytics.presets';
const SETTINGS_KEY = 'flowsense.analytics.settings';
const LIVE_SYNC_GAP_MS = 120000;
const DAY_MS = 86400000;

const readJson = (key, fallback) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch (_) { return fallback; } };
const writeJson = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* storage blocked */ } };
const ymd = (d) => new Date(d).toISOString().slice(0, 10);
const parseYmd = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const makeRange = (startDate, endDate) => ({ startDate, endDate, start: ymd(startDate), end: ymd(endDate) });

const QUICK_RANGES = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: '6months', label: 'Last 6 Months' },
  { value: '1year', label: 'Last Year' },
  { value: 'ytd', label: 'Year to Date' },
];

function rangeFor(value) {
  const endDate = new Date();
  const startDate = new Date();
  switch (value) {
    case '7days': startDate.setDate(endDate.getDate() - 7); break;
    case '30days': startDate.setDate(endDate.getDate() - 30); break;
    case '90days': startDate.setDate(endDate.getDate() - 90); break;
    case '6months': startDate.setMonth(endDate.getMonth() - 6); break;
    case '1year': startDate.setFullYear(endDate.getFullYear() - 1); break;
    case 'ytd': startDate.setMonth(0, 1); break;
    default: startDate.setDate(endDate.getDate() - 30); break;
  }
  return makeRange(startDate, endDate);
}

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** "AI Intelligence" tab: every model and engine, the same live overview as the AI Intelligence Center. */
function AIIntelligenceTab({ onAskAI }) {
  const toast = useToast();
  const navigate = useNavigate();
  const user = useMemo(() => currentUser(), []);
  const canManage = canManageModels(user);
  const { data, loading, error, refresh } = useMlOverview();
  const [trainingKey, setTrainingKey] = useState(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const onTrain = useCallback(async (key) => {
    setTrainingKey(key);
    try {
      const job = await mlOpsService.train(key);
      toast({ title: job.alreadyActive ? 'Already training' : 'Training started', description: `Run ${job.id}. Progress appears on the model card.`, status: job.alreadyActive ? 'info' : 'success', duration: 4000 });
      refresh();
    } catch (e) {
      toast({ title: 'Could not start training', description: e.message, status: 'error', duration: 6000 });
    } finally {
      if (mounted.current) setTrainingKey(null);
    }
  }, [refresh, toast]);
  const onOpenModel = useCallback((key) => navigate(`/admin/ai/grading?tab=registry&model=${encodeURIComponent(key)}`), [navigate]);

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
        <Text fontSize="sm" color="gray.500">Every FlowSense model and AI engine, live. Training, promotion and review live in the AI Intelligence Center.</Text>
        <HStack>
          <Button size="sm" variant="outline" leftIcon={<MdAutoAwesome />} colorScheme="purple" onClick={() => onAskAI?.('How do the FlowSense AI models work together, and which dashboards use each one?')}>Explain the AI</Button>
          <Button size="sm" colorScheme="purple" leftIcon={<MdPsychology />} onClick={() => navigate('/admin/ai/grading')}>Open AI Intelligence Center</Button>
        </HStack>
      </Flex>
      <IntelligenceOverview overview={data} loading={loading} error={error} onRetry={refresh} canManage={canManage} onTrain={onTrain} onOpenModel={onOpenModel} trainingKey={trainingKey} />
    </VStack>
  );
}
// --- END UPDATE ---

function AnalyticsPage() {
  // Theme colors
  const { colorMode } = useColorMode();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const headerBg = useColorModeValue('white', 'gray.800');
  const tabBg = useColorModeValue('white', 'gray.800');
  const tabSelectedBg = useColorModeValue('blue.50', 'blue.900');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  // Fix for Line 743: Define the hover value here at the top level
  const presetHoverBg = useColorModeValue('gray.100', 'gray.600');

  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // --- UPDATE: tabs with stable slugs (URL ?tab=) and distinct icons ---
  const dashboardTabs = useMemo(() => [
    { slug: 'executive',        name: 'Executive Overview',     icon: MdDashboard,       color: 'blue',   focus: 'executive' },
    { slug: 'sla',              name: 'SLA Compliance',         icon: MdTimer,           color: 'cyan',   focus: 'sla' },
    { slug: 'work-orders',      name: 'Work Orders',            icon: MdAssignment,      color: 'purple', focus: 'workorders' },
    { slug: 'assets',           name: 'Asset Performance',      icon: MdTrendingUp,      color: 'green',  focus: 'assets' },
    { slug: 'inventory',        name: 'Inventory Intelligence', icon: MdInventory,       color: 'orange', focus: 'inventory' },
    { slug: 'predictive-health',name: 'Predictive Health',      icon: MdHealthAndSafety, color: 'red',    focus: 'predictive' },
    { slug: 'repair-replace',   name: 'Repair vs Replace',      icon: MdAttachMoney,     color: 'yellow', focus: 'lifecycle' },
    { slug: 'ai-learning',      name: 'AI & Learning',          icon: MdPsychology,      color: 'teal',   focus: 'ai' },
    { slug: 'ml-health',        name: 'ML Engine Health',       icon: MdMonitorHeart,    color: 'pink',   focus: 'ml-health' },
    { slug: 'ai-intelligence',  name: 'AI Intelligence',        icon: MdHub,             color: 'purple', focus: 'ai' },
  ], []);
  const tabFromUrl = Math.max(0, dashboardTabs.findIndex(t => t.slug === params.get('tab')));
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  // --- END UPDATE ---

  // State management
  // --- UPDATE: range from the URL (?from=&to=) when present ---
  const [dateRange, setDateRange] = useState(() => {
    const from = parseYmd(params.get('from'));
    const to = parseYmd(params.get('to'));
    if (from && to && from <= to) return makeRange(from, to);
    return rangeFor('30days');
  });
  const [quickRange, setQuickRange] = useState(() => (params.get('from') ? null : '30days'));
  // --- END UPDATE ---

  // --- UPDATE: persisted settings ---
  const [settings, setSettings] = useState(() => ({ autoRefresh: false, refreshInterval: 300000, liveSync: true, showBriefing: true, ...readJson(SETTINGS_KEY, {}) }));
  const { autoRefresh, refreshInterval, liveSync, showBriefing } = settings;
  const updateSettings = useCallback((patch) => setSettings((s) => { const next = { ...s, ...patch }; writeJson(SETTINGS_KEY, next); return next; }), []);
  const [savedPresets, setSavedPresets] = useState(() => readJson(PRESETS_KEY, []));
  const [presetName, setPresetName] = useState('');
  // --- END UPDATE ---

  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // --- UPDATE (AI drawer) ---
  const [askState, setAskState] = useState({ key: 0, question: null });
  // --- END UPDATE ---

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const { isOpen: isPresetsOpen, onOpen: onPresetsOpen, onClose: onPresetsClose } = useDisclosure();
  // --- UPDATE ---
  const { isOpen: isSaveOpen, onOpen: onSaveOpen, onClose: onSaveClose } = useDisclosure();
  const { isOpen: isFeedOpen, onOpen: onFeedOpen, onClose: onFeedClose } = useDisclosure();
  const { isOpen: isAskOpen, onOpen: onAskOpen, onClose: onAskClose } = useDisclosure();
  const live = useAnalyticsLive();
  const { markRead } = live;
  const liveMeta = STATUS_META[live.status] || STATUS_META.idle;
  // --- END UPDATE ---

  // Format date helper
  const formatDate = (date) => ymd(date);

  // Calculate date range stats
  const dateRangeStats = useMemo(() => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const daysDiff = Math.ceil((end - start) / DAY_MS);
    return {
      days: daysDiff,
      weeks: Math.ceil(daysDiff / 7),
      months: Math.ceil(daysDiff / 30),
      isValid: daysDiff > 0,
    };
  }, [dateRange]);

  // --- UPDATE: keep the URL in sync (replace, so Back still leaves the page) ---
  useEffect(() => {
    const slug = dashboardTabs[activeTab]?.slug || 'executive';
    setParams((prev) => {
      if (prev.get('tab') === slug && prev.get('from') === dateRange.start && prev.get('to') === dateRange.end) return prev;
      const next = new URLSearchParams(prev);
      next.set('tab', slug);
      next.set('from', dateRange.start);
      next.set('to', dateRange.end);
      return next;
    }, { replace: true });
  }, [activeTab, dateRange.start, dateRange.end, dashboardTabs, setParams]);
  // --- END UPDATE ---

  // Handle date change
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newDate = parseYmd(value);
    // Ensure the date object is valid before updating state
    if (!newDate) return;
    setDateRange(prevRange => makeRange(name === 'startDate' ? newDate : prevRange.startDate, name === 'endDate' ? newDate : prevRange.endDate));
    setQuickRange(null);
  };

  // Quick date range presets
  const applyQuickRange = (range) => {
    setDateRange(rangeFor(range));
    setQuickRange(range);
    toast({
      title: 'Date Range Updated',
      description: `Applied ${QUICK_RANGES.find(r => r.value === range)?.label || range}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // --- UPDATE: presets persisted, named in a dialog, deletable ---
  const savePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const newPreset = { name, start: dateRange.start, end: dateRange.end, savedAt: new Date().toISOString() };
    setSavedPresets(prev => {
      const next = [newPreset, ...prev.filter(p => p.name !== name)].slice(0, 20);
      writeJson(PRESETS_KEY, next);
      return next;
    });
    setPresetName('');
    onSaveClose();
    toast({ title: 'Preset Saved', description: `"${name}" saved`, status: 'success', duration: 3000, isClosable: true });
  };

  const deletePreset = (name) => {
    setSavedPresets(prev => { const next = prev.filter(p => p.name !== name); writeJson(PRESETS_KEY, next); return next; });
  };

  // Load preset
  const loadPreset = (preset) => {
    const s = parseYmd(preset.start || ymd(preset.startDate));
    const e = parseYmd(preset.end || ymd(preset.endDate));
    if (!s || !e) return;
    setDateRange(makeRange(s, e));
    setQuickRange(null);
    toast({ title: 'Preset Loaded', description: `Loaded "${preset.name}"`, status: 'info', duration: 2000, isClosable: true });
    onPresetsClose();
  };
  // --- END UPDATE ---

  // Manual refresh
  const handleRefresh = useCallback((silent = false) => {
    setRefreshKey(prev => prev + 1);
    setLastRefresh(new Date());
    if (!silent) {
      toast({ title: 'Refreshing Data', description: 'Dashboards are being updated', status: 'info', duration: 2000, isClosable: true });
    }
  }, [toast]);

  // --- UPDATE: real exports ---
  const exportKpiCsv = async () => {
    try {
      const daysBack = Math.min(Math.ceil((Date.now() - new Date(dateRange.startDate).getTime()) / DAY_MS) + 1, 366);
      const h = await mlOpsService.getKpiHistory(Math.max(daysBack, 7));
      const keys = Object.keys(h.series || {});
      const dates = new Set();
      keys.forEach(k => h.series[k].forEach(p => { if (p.date >= dateRange.start && p.date <= dateRange.end) dates.add(p.date); }));
      const byKey = Object.fromEntries(keys.map(k => [k, Object.fromEntries(h.series[k].map(p => [p.date, p.value]))]));
      const lines = [['date', ...keys].join(',')];
      [...dates].sort().forEach(d => lines.push([d, ...keys.map(k => csvCell(byKey[k][d]))].join(',')));
      if (h.unavailable?.length) lines.push('', `# Not tracked: ${h.unavailable.join(' ')}`);
      downloadBlob(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }), `flowsense-kpis-${dateRange.start}_${dateRange.end}.csv`);
      toast({ title: 'Export ready', description: `${dates.size} days of KPI history downloaded.`, status: 'success', duration: 3000 });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, status: 'error', duration: 5000 });
    }
  };

  const exportBriefing = async () => {
    try {
      const b = await mlOpsService.getBriefing({ startDate: dateRange.start, endDate: dateRange.end, focus: dashboardTabs[activeTab]?.focus });
      downloadBlob(new Blob([JSON.stringify(b, null, 2)], { type: 'application/json' }), `flowsense-ai-briefing-${dateRange.start}_${dateRange.end}.json`);
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, status: 'error', duration: 5000 });
    }
  };
  // --- END UPDATE ---

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return; // --- UPDATE: skip while hidden
      handleRefresh(true);
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, handleRefresh]);

  // --- UPDATE: live sync — relevant events refetch the simple dashboards (bounded) ---
  const lastLiveSync = useRef(Date.now());
  const liveSyncTimer = useRef(null);
  useEffect(() => {
    if (!liveSync || !live.version) return undefined;
    if (liveSyncTimer.current) return undefined;
    const wait = Math.max(0, LIVE_SYNC_GAP_MS - (Date.now() - lastLiveSync.current));
    liveSyncTimer.current = setTimeout(() => {
      liveSyncTimer.current = null;
      lastLiveSync.current = Date.now();
      handleRefresh(true);
    }, wait);
    return undefined;
  }, [live.version, liveSync, handleRefresh]);
  useEffect(() => () => { if (liveSyncTimer.current) clearTimeout(liveSyncTimer.current); }, []);
  // --- END UPDATE ---

  // Tab change handler
  const handleTabChange = useCallback((index) => setActiveTab(index), []);

  // --- UPDATE: Ask FlowSense AI ---
  const openAsk = useCallback((question = null) => {
    setAskState(s => ({ key: s.key + 1, question }));
    onAskOpen();
  }, [onAskOpen]);
  const onNavigate = useCallback((path) => {
    if (!path) return;
    const m = /^\/admin\/analytics\?tab=([\w-]+)/.exec(path);
    if (m) {
      const idx = dashboardTabs.findIndex(t => t.slug === m[1]);
      if (idx >= 0) { setActiveTab(idx); window.scrollTo?.({ top: 0, behavior: 'smooth' }); return; }
    }
    navigate(path);
  }, [dashboardTabs, navigate]);
  const openFeed = useCallback(() => { onFeedOpen(); markRead(); }, [onFeedOpen, markRead]);

  // keyboard: Alt+1…Alt+0 tabs, Alt+R refresh
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (/^Digit[0-9]$/.test(e.code)) {
        const n = Number(e.code.slice(5));
        const idx = n === 0 ? 9 : n - 1;
        if (idx < dashboardTabs.length) { e.preventDefault(); setActiveTab(idx); }
      } else if (e.code === 'KeyR') {
        e.preventDefault(); handleRefresh();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dashboardTabs.length, handleRefresh]);
  // --- END UPDATE ---

  // Validate date range
  useEffect(() => {
    if (!dateRangeStats.isValid) {
      toast({
        title: 'Invalid Date Range',
        description: 'End date must be after start date',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [dateRangeStats.isValid, toast]);

  // --- UPDATE: what every dashboard receives ---
  const shared = {
    dateRange,
    liveVersion: live.version,
    lastLiveEvent: live.lastEvent,
    onAskAI: openAsk,
  };
  const currentTab = dashboardTabs[activeTab] || dashboardTabs[0];
  // --- END UPDATE ---

  return (
    <Box bg={bgColor} minH="100vh" pt={{ base: '130px', md: '80px', xl: '80px' }} pb={8}>
      <Box maxW="1600px" mx="auto" px={{ base: 4, md: 6, lg: 8 }}>

        {/* ================================================================ */}
        {/* HEADER                                                           */}
        {/* ================================================================ */}
        <Box
          bg={headerBg}
          borderRadius="xl"
          boxShadow="xl"
          p={6}
          mb={6}
          borderWidth="1px"
          borderColor={borderColor}
          position="relative"
          overflow="hidden"
        >
          {/* Decorative gradient overlay */}
          <Box position="absolute" top="0" left="0" right="0" height="4px" bgGradient="linear(to-r, blue.400, purple.400, pink.400)" opacity="0.8" />

          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={3}>
              <Box p={2} bg={iconBg} borderRadius="lg">
                <Icon as={MdAnalytics} boxSize={8} color="blue.500" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Heading as="h1" size="lg" color={headingColor}>
                  FlowSense Analytics Hub
                </Heading>
                <HStack spacing={2} mt={1} flexWrap="wrap">
                  <Badge colorScheme={currentTab.color} fontSize="xs">{currentTab.name}</Badge>
                  <Badge colorScheme="green" fontSize="xs">{dateRangeStats.days} Days</Badge>
                  {autoRefresh && <Badge colorScheme="purple" fontSize="xs">Auto-refresh</Badge>}
                  {/* --- UPDATE: live status --- */}
                  <Tooltip label={`Websocket: ${liveMeta.label}. ${liveSync ? 'Live sync is on — dashboards refresh after relevant events.' : 'Live sync is off.'}`} hasArrow>
                    <Badge colorScheme={liveMeta.color} fontSize="xs" cursor="pointer" onClick={openFeed} data-testid="live-status">
                      <HStack spacing={1}>
                        <Box w="6px" h="6px" borderRadius="full" bg={`${liveMeta.color}.400`} />
                        <Text as="span">{liveMeta.label}</Text>
                      </HStack>
                    </Badge>
                  </Tooltip>
                  {/* --- END UPDATE --- */}
                </HStack>
              </VStack>
            </HStack>

            <HStack spacing={2} flexWrap="wrap">
              <Tooltip label="Last updated" hasArrow>
                <Badge colorScheme="gray" fontSize="xs" px={3} py={1}>{lastRefresh.toLocaleTimeString()}</Badge>
              </Tooltip>
              {/* --- UPDATE: AI + live activity --- */}
              <FlowSenseAIGuide path="/admin/analytics" tab={currentTab.slug} liveVersion={live.version} onAsk={openAsk} />
              <Button size="sm" colorScheme="purple" leftIcon={<MdAutoAwesome />} onClick={() => openAsk(null)} data-guide="ask-ai">Ask FlowSense AI</Button>
              <Tooltip label="Live AI activity" hasArrow>
                <Box position="relative" data-guide="live-feed">
                  <IconButton icon={<MdBolt />} size="sm" variant="outline" onClick={openFeed} aria-label="Live AI activity" />
                  {live.unread > 0 && (
                    <Badge position="absolute" top="-6px" right="-6px" colorScheme="red" borderRadius="full" fontSize="2xs" px={1.5} data-testid="live-unread">
                      {live.unread > 99 ? '99+' : live.unread}
                    </Badge>
                  )}
                </Box>
              </Tooltip>
              {/* --- END UPDATE --- */}
              <Tooltip label="Refresh dashboards (Alt+R)" hasArrow>
                <IconButton icon={<MdRefresh />} size="sm" variant="outline" onClick={() => handleRefresh()} aria-label="Refresh" />
              </Tooltip>
              {/* --- UPDATE: export menu --- */}
              <Menu placement="bottom-end">
                <Tooltip label="Export" hasArrow>
                  <MenuButton as={IconButton} icon={<MdDownload />} size="sm" variant="outline" aria-label="Export" />
                </Tooltip>
                <MenuList fontSize="sm">
                  <MenuItem icon={<MdTableChart />} onClick={exportKpiCsv}>KPI history (CSV)</MenuItem>
                  <MenuItem icon={<MdDataObject />} onClick={exportBriefing}>AI briefing (JSON)</MenuItem>
                  <MenuDivider />
                  <MenuItem icon={<MdPrint />} onClick={() => window.print()}>Print / save as PDF</MenuItem>
                </MenuList>
              </Menu>
              {/* --- END UPDATE --- */}
              <Tooltip label="Settings" hasArrow>
                <IconButton icon={<MdSettings />} size="sm" variant="outline" onClick={onSettingsOpen} aria-label="Settings" />
              </Tooltip>
            </HStack>
          </Flex>
        </Box>

        {/* ================================================================ */}
        {/* DATE RANGE CONTROLS                                              */}
        {/* ================================================================ */}
        <Box bg={cardBg} borderRadius="xl" boxShadow="md" p={5} mb={6} borderWidth="1px" borderColor={borderColor} data-guide="date-range">
          <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <HStack spacing={2}>
                <Icon as={MdCalendarToday} color="blue.500" boxSize={5} />
                <Heading size="sm" color={headingColor}>Date Range Filter</Heading>
              </HStack>
              <HStack spacing={2}>
                <Button size="sm" variant="ghost" leftIcon={<MdHistory />} onClick={onPresetsOpen} isDisabled={savedPresets.length === 0}>
                  Presets ({savedPresets.length})
                </Button>
                <Button size="sm" variant="ghost" leftIcon={<MdBookmark />} onClick={onSaveOpen}>
                  Save Preset
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  rightIcon={showAdvanced ? <ChevronDownIcon /> : <ChevronDownIcon transform="rotate(180deg)" />}
                >
                  {showAdvanced ? 'Hide' : 'Show'} Advanced
                </Button>
              </HStack>
            </Flex>

            {/* Quick Presets */}
            <SimpleGrid columns={{ base: 2, sm: 3, md: 6 }} spacing={2}>
              {QUICK_RANGES.map((preset) => (
                <Button
                  key={preset.value}
                  size="sm"
                  variant={quickRange === preset.value ? 'solid' : 'outline'}
                  colorScheme={quickRange === preset.value ? 'blue' : 'gray'}
                  onClick={() => applyQuickRange(preset.value)}
                >
                  {preset.label}
                </Button>
              ))}
            </SimpleGrid>

            {/* Date Inputs */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel htmlFor="startDate" fontSize="sm" color={textColor}>Start Date</FormLabel>
                <Input id="startDate" type="date" name="startDate" value={formatDate(dateRange.startDate)} max={formatDate(dateRange.endDate)}
                  onChange={handleDateChange} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }} />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="endDate" fontSize="sm" color={textColor}>End Date</FormLabel>
                <Input id="endDate" type="date" name="endDate" value={formatDate(dateRange.endDate)} min={formatDate(dateRange.startDate)} max={ymd(new Date())}
                  onChange={handleDateChange} bg={inputBg} borderColor={borderColor} _hover={{ borderColor: 'blue.400' }} />
              </FormControl>
            </SimpleGrid>

            {/* Date Range Stats */}
            {dateRangeStats.isValid && (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                <Stat bg={statBg} p={3} borderRadius="md" size="sm"><StatLabel fontSize="xs">Days</StatLabel><StatNumber fontSize="xl">{dateRangeStats.days}</StatNumber></Stat>
                <Stat bg={statBg} p={3} borderRadius="md" size="sm"><StatLabel fontSize="xs">Weeks</StatLabel><StatNumber fontSize="xl">{dateRangeStats.weeks}</StatNumber></Stat>
                <Stat bg={statBg} p={3} borderRadius="md" size="sm"><StatLabel fontSize="xs">Months</StatLabel><StatNumber fontSize="xl">{dateRangeStats.months}</StatNumber></Stat>
                <Stat bg={statBg} p={3} borderRadius="md" size="sm"><StatLabel fontSize="xs">Status</StatLabel><StatNumber fontSize="xl"><Badge colorScheme="green">Valid</Badge></StatNumber></Stat>
              </SimpleGrid>
            )}

            {/* Advanced Options */}
            <Collapse in={showAdvanced} animateOpacity>
              <Box mt={4} p={4} bg={statBg} borderRadius="md">
                <VStack align="stretch" spacing={3}>
                  <Text fontWeight="bold" fontSize="sm" color={headingColor}>Advanced Options</Text>
                  <HStack justify="space-between">
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm" color={textColor}>Auto-refresh global wrapper</FormLabel>
                      <Switch id="auto-refresh" colorScheme="blue" isChecked={autoRefresh} onChange={(e) => updateSettings({ autoRefresh: e.target.checked })} />
                    </FormControl>
                    {autoRefresh && (
                      <Select size="sm" maxW="200px" value={refreshInterval} onChange={(e) => updateSettings({ refreshInterval: Number(e.target.value) })} bg={inputBg}>
                        <option value={60000}>1 minute</option>
                        <option value={300000}>5 minutes</option>
                        <option value={600000}>10 minutes</option>
                        <option value={1800000}>30 minutes</option>
                      </Select>
                    )}
                  </HStack>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="live-sync" mb="0" fontSize="sm" color={textColor}>Live sync (refresh after AI / sensor events, at most every 2 min)</FormLabel>
                    <Switch id="live-sync" colorScheme="purple" isChecked={liveSync} onChange={(e) => updateSettings({ liveSync: e.target.checked })} />
                  </FormControl>
                  <Alert status="info" fontSize="xs" borderRadius="md">
                    <AlertIcon />
                    Date range applies to all dashboards. Auto-refresh and live sync reload the simple widgets; complex ML dashboards manage their own WebSocket connections and receive every live event as a prop.
                  </Alert>
                </VStack>
              </Box>
            </Collapse>
          </VStack>
        </Box>

        {/* ================================================================ */}
        {/* --- UPDATE: AI BRIEFING                                          */}
        {/* ================================================================ */}
        {showBriefing && dateRangeStats.isValid && (
          <Box data-guide="ai-briefing">
          <AnalyticsAIBriefing
            dateRange={dateRange}
            focus={currentTab.focus}
            liveVersion={live.version}
            onAsk={openAsk}
            onNavigate={onNavigate}
          />
          </Box>
        )}
        {/* --- END UPDATE --- */}

        {/* ================================================================ */}
        {/* DASHBOARD TABS                                                   */}
        {/* ================================================================ */}
        <Box bg={tabBg} borderRadius="xl" boxShadow="md" borderWidth="1px" borderColor={borderColor}>
          <Tabs index={activeTab} onChange={handleTabChange} isLazy variant="soft-rounded" colorScheme="blue">
            <Box p={4} borderBottomWidth="1px" borderColor={borderColor} overflowX="auto" data-guide="tabs">
              <TabList flexWrap="wrap" gap={2}>
                {dashboardTabs.map((tab, index) => (
                    <Tab
                      key={tab.slug}
                      title={index < 10 ? `${tab.name} (Alt+${index === 9 ? 0 : index + 1})` : tab.name}
                      _selected={{
                        bg: tabSelectedBg,
                        // Fix for Line 608: Use ternary with colorMode instead of calling hook
                        color: colorMode === 'light' ? `${tab.color}.600` : `${tab.color}.300`,
                      }}
                      fontSize="sm"
                      fontWeight="medium"
                    >
                      <HStack spacing={2}>
                        <Icon as={tab.icon} />
                        <Text>{tab.name}</Text>
                      </HStack>
                    </Tab>
                ))}
              </TabList>
            </Box>

            <TabPanels>
              {/* Simple dashboards retain the refreshKey to allow force-refetching on unmount */}
              <TabPanel p={6}>
                <ExecutiveOverviewDashboard {...shared} key={`exec-${refreshKey}`} />
              </TabPanel>
              <TabPanel p={6}>
                <SlaComplianceDashboard {...shared} key={`sla-${refreshKey}`} />
              </TabPanel>

              {/* Complex dashboards drop the key prop so WebSockets/Cache survive parent refreshes */}
              <TabPanel p={6}>
                <WorkOrderDeepDiveDashboard {...shared} />
              </TabPanel>
              <TabPanel p={6}>
                <AssetPerformanceDashboard {...shared} />
              </TabPanel>
              <TabPanel p={6}>
                <InventoryIntelligenceDashboard {...shared} key={`inv-${refreshKey}`} />
              </TabPanel>
              <TabPanel p={6}>
                <PredictiveHealthDashboard {...shared} key={`pred-${refreshKey}`} />
              </TabPanel>
              <TabPanel p={6}>
                <Box>
                  <Heading size="md" mb={4} color={headingColor}>Repair vs. Replace Analysis</Heading>
                  <Text mb={6} color={mutedColor}>
                    AI-driven financial recommendations based on asset lifecycle and maintenance history.
                  </Text>
                  <LifecycleCostWidget {...shared} key={`lcca-${refreshKey}`} />
                </Box>
              </TabPanel>

              {/* Heavy ML Dashboards manage their own lifecycles */}
              <TabPanel p={6}>
                <AILearningDashboard {...shared} />
              </TabPanel>
              <TabPanel p={6}>
                <MlHealthDashboard {...shared} />
              </TabPanel>
              {/* --- UPDATE: AI Intelligence --- */}
              <TabPanel p={6}>
                <AIIntelligenceTab onAskAI={openAsk} />
              </TabPanel>
              {/* --- END UPDATE --- */}
            </TabPanels>
          </Tabs>
        </Box>

        {/* ================================================================ */}
        {/* SETTINGS MODAL                                                   */}
        {/* ================================================================ */}
        <Modal isOpen={isSettingsOpen} onClose={onSettingsClose} size="lg">
          <ModalOverlay />
          <ModalContent bg={cardBg}>
            <ModalHeader color={headingColor}>Analytics Settings</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb="0" color={textColor}>Auto-refresh</FormLabel>
                  <Switch colorScheme="blue" isChecked={autoRefresh} onChange={(e) => updateSettings({ autoRefresh: e.target.checked })} />
                </FormControl>
                {autoRefresh && (
                  <FormControl>
                    <FormLabel color={textColor}>Refresh Interval</FormLabel>
                    <Select value={refreshInterval} onChange={(e) => updateSettings({ refreshInterval: Number(e.target.value) })} bg={inputBg}>
                      <option value={60000}>Every minute</option>
                      <option value={300000}>Every 5 minutes</option>
                      <option value={600000}>Every 10 minutes</option>
                      <option value={1800000}>Every 30 minutes</option>
                    </Select>
                  </FormControl>
                )}
                {/* --- UPDATE --- */}
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <FormLabel mb="0" color={textColor}>Live sync</FormLabel>
                    <FormHelperText mt={0}>Refresh dashboards after model, prediction and sensor events (at most every 2 minutes).</FormHelperText>
                  </Box>
                  <Switch colorScheme="purple" isChecked={liveSync} onChange={(e) => updateSettings({ liveSync: e.target.checked })} />
                </FormControl>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb="0" color={textColor}>Show the AI briefing</FormLabel>
                  <Switch colorScheme="purple" isChecked={showBriefing} onChange={(e) => updateSettings({ showBriefing: e.target.checked })} />
                </FormControl>
                {/* --- END UPDATE --- */}

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={2} color={headingColor}>Cache Information</Text>
                  <VStack align="stretch" spacing={2} fontSize="sm" color={textColor}>
                    <HStack justify="space-between"><Text>Last refresh:</Text><Text fontWeight="medium">{lastRefresh.toLocaleString()}</Text></HStack>
                    <HStack justify="space-between"><Text>Active dashboard:</Text><Badge colorScheme="blue">{currentTab.name}</Badge></HStack>
                    <HStack justify="space-between"><Text>Date range:</Text><Text fontWeight="medium">{dateRangeStats.days} days</Text></HStack>
                    {/* --- UPDATE --- */}
                    <HStack justify="space-between"><Text>Live connection:</Text><Badge colorScheme={liveMeta.color}>{liveMeta.label}</Badge></HStack>
                    <HStack justify="space-between"><Text>Live events this session:</Text><Text fontWeight="medium">{Object.values(live.counts).reduce((a, b) => a + b, 0)}</Text></HStack>
                    {/* --- END UPDATE --- */}
                  </VStack>
                </Box>

                {/* --- UPDATE --- */}
                <Box fontSize="sm" color={textColor}>
                  <Text fontWeight="bold" mb={1} color={headingColor}>Keyboard</Text>
                  <Text><Kbd>Alt</Kbd> + <Kbd>1</Kbd>…<Kbd>0</Kbd> switch dashboards · <Kbd>Alt</Kbd> + <Kbd>R</Kbd> refresh</Text>
                </Box>
                {/* --- END UPDATE --- */}

                <Button leftIcon={<MdCached />} onClick={() => handleRefresh()} colorScheme="blue" variant="outline" w="full">
                  Refresh All Dashboards
                </Button>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onSettingsClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* ================================================================ */}
        {/* --- UPDATE: SAVE PRESET DIALOG (replaces window.prompt)          */}
        {/* ================================================================ */}
        <Modal isOpen={isSaveOpen} onClose={onSaveClose} size="sm">
          <ModalOverlay />
          <ModalContent bg={cardBg}>
            <ModalHeader color={headingColor}>Save date range</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel fontSize="sm">Preset name</FormLabel>
                <Input autoFocus value={presetName} maxLength={60} placeholder="e.g. Hurricane season 2026"
                  onChange={(e) => setPresetName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') savePreset(); }} />
                <FormHelperText>{dateRange.start} to {dateRange.end}. A preset with the same name is replaced.</FormHelperText>
              </FormControl>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button variant="ghost" onClick={onSaveClose}>Cancel</Button>
              <Button colorScheme="blue" onClick={savePreset} isDisabled={!presetName.trim()}>Save</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {/* --- END UPDATE --- */}

        {/* ================================================================ */}
        {/* PRESETS MODAL                                                    */}
        {/* ================================================================ */}
        <Modal isOpen={isPresetsOpen} onClose={onPresetsClose} size="md">
          <ModalOverlay />
          <ModalContent bg={cardBg}>
            <ModalHeader color={headingColor}>Saved Date Range Presets</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {savedPresets.length > 0 ? (
                <VStack spacing={3} align="stretch">
                  {savedPresets.map((preset) => (
                    <Box
                      key={preset.name}
                      p={3}
                      bg={statBg}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                      cursor="pointer"
                      // Fix for Line 743: Use pre-defined value from top level
                      _hover={{ bg: presetHoverBg }}
                      onClick={() => loadPreset(preset)}
                    >
                      <Flex justify="space-between" align="center">
                        <VStack align="flex-start" spacing={1}>
                          <Text fontWeight="bold" fontSize="sm" color={headingColor}>{preset.name}</Text>
                          <Text fontSize="xs" color={mutedColor}>{preset.start || ymd(preset.startDate)} to {preset.end || ymd(preset.endDate)}</Text>
                          <Text fontSize="xs" color={mutedColor}>Saved: {new Date(preset.savedAt).toLocaleDateString()}</Text>
                        </VStack>
                        <HStack>
                          <Icon as={MdHistory} color="blue.500" boxSize={5} />
                          {/* --- UPDATE --- */}
                          <IconButton size="xs" variant="ghost" colorScheme="red" icon={<MdDelete />} aria-label={`Delete ${preset.name}`}
                            onClick={(e) => { e.stopPropagation(); deletePreset(preset.name); }} />
                          {/* --- END UPDATE --- */}
                        </HStack>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <VStack align="flex-start" spacing={1}>
                    <Text fontWeight="bold">No saved presets</Text>
                    <Text fontSize="sm">Save your current date range to create a preset</Text>
                  </VStack>
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onPresetsClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* ================================================================ */}
        {/* --- UPDATE: LIVE AI ACTIVITY + ASK FLOWSENSE AI                  */}
        {/* ================================================================ */}
        <AnalyticsLiveFeed
          isOpen={isFeedOpen}
          onClose={onFeedClose}
          feed={live.feed}
          status={live.status}
          loading={live.loadingFeed}
          error={live.feedError}
          onRefresh={live.refreshFeed}
          onNavigate={(path) => { onFeedClose(); onNavigate(path); }}
        />

        <Drawer isOpen={isAskOpen} onClose={onAskClose} placement="right" size="md">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">
              <HStack><Icon as={MdAutoAwesome} color="purple.400" /><Text>Ask FlowSense AI</Text></HStack>
              <Text fontSize="xs" color={mutedColor} fontWeight="normal" mt={1}>
                About the Analytics Hub, its dashboards and the AI models behind them. Answers use live FlowSense data.
              </Text>
            </DrawerHeader>
            <DrawerBody py={4}>
              <FlowSenseAIExplainer
                key={askState.key}
                focus="analytics"
                initialQuestion={askState.question}
                height="calc(100vh - 170px)"
                intro={`Viewing: ${currentTab.name}, ${dateRange.start} to ${dateRange.end}.`}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        {/* --- END UPDATE --- */}

      </Box>
    </Box>
  );
}

export default AnalyticsPage;
